<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import DateRange from '../../DateRange.svelte';

  import PayoutStatusBadge from '../../PayoutStatusBadge.svelte';
  import PayrollTransactionHistoryService, { 
    type PayrollTransactionHistory, 
    type PayrollTransactionHistoryFilters 
  } from '../../../lib/api/admin/payroll-transaction-history.service';
  import toast from 'svelte-french-toast';

  // State
  let isLoading = true;
  let error = '';
  let transactions: PayrollTransactionHistory[] = [];
  let totalPages = 1;
  let currentPage = 1;
  let totalItems = 0;
  let itemsPerPage = 15;

  // Filters
  let startDate = '';
  let endDate = '';
  let selectedEmployer = '';

  // Employer options (this would typically come from an API)
  let employerOptions: Array<{ uuid: string; name: string }> = [
    { uuid: '', name: 'All Employers' }
  ];

  const tableColumns = [
    { 
      key: 'name', 
      label: 'Employer', 
      sortable: true,
      render: (value: any, row: any) => row.name || 'N/A'
    },
    { 
      key: 'total_payout', 
      label: 'Amount', 
      sortable: true,
      render: (value: any, row: any) => PayrollTransactionHistoryService.formatCurrency(row.total_payout || 0)
    },
    { 
      key: 'transaction_date', 
      label: 'Date', 
      sortable: true,
      render: (value: any, row: any) => PayrollTransactionHistoryService.formatDate(row.transaction_date)
    },
    { 
      key: 'donation_payout_status',
      label: 'Status',
      align: 'center',
      sortable: true,
      render: (value: any, row: any) => renderStatus(row.status_name || row.donation_payout_status)
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      sortable: false, 
      render: (value: any, row: any) => renderActions(value, row)
    }
  ];

  // Reactive statement to ensure transactions is always an array
  $: if (!Array.isArray(transactions)) {
    transactions = [];
  }

  onMount(async () => {
    // Load employer options first
    await loadEmployerOptions();
    // Then load transactions
    loadTransactions();
  });

  // Add status badges after table update
  afterUpdate(() => {
    // Handle status badges
    const statusWrappers = document.querySelectorAll('.status-badge-wrapper');
    statusWrappers.forEach((wrapper) => {
      if (!wrapper.hasChildNodes()) {
        const status = wrapper.getAttribute('data-status');
        const statusBadge = document.createElement('div');
        wrapper.appendChild(statusBadge);
        
        // Create a new instance of PayoutStatusBadge component
        new PayoutStatusBadge({
          target: statusBadge,
          props: {
            status: status as any
          }
        });
      }
    });
  });

  async function loadTransactions() {
    try {
      isLoading = true;
      error = '';
      
      const filters: PayrollTransactionHistoryFilters = {
        page: Math.max(currentPage, 1), // API expects 1-based pagination
        per_page: itemsPerPage
      };
      
      // Add filters if they exist
      if (selectedEmployer) filters.employer_uuid = selectedEmployer;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      
      const response = await PayrollTransactionHistoryService.getPayrollTransactionHistory(filters);
      
      if (response.success && response.data) {
        transactions = response.data || [];
        totalItems = response.paging?.total || 0;
        totalPages = response.paging?.last_page || 1;
        // API returns current_page as 0-based but expects requests with 1-based page numbers
        currentPage = Math.max((response.paging?.current_page || 0) + 1, 1);
      } else {
        error = response.message || 'Failed to load transactions';
        toast.error(error);
        transactions = [];
      }
    } catch (err) {
      error = 'Failed to load transactions';
      toast.error(error);
      console.error('Error loading transactions:', err);
      transactions = [];
      totalItems = 0;
      totalPages = 1;
    } finally {
      isLoading = false;
    }
  }

  // Load employer options for dropdown
  async function loadEmployerOptions() {
    try {
      // Get all transactions without filters to populate employer options
      const allTransactionsResponse = await PayrollTransactionHistoryService.getPayrollTransactionHistory({
        per_page: 1000 // Get a large number to ensure we get all employers
      });
      
      if (allTransactionsResponse.success && allTransactionsResponse.data) {
        // Extract unique employers from all transactions data
        const uniqueEmployers = new Map<string, string>();
        allTransactionsResponse.data.forEach(transaction => {
          if (transaction.uuid && transaction.name) {
            if (!uniqueEmployers.has(transaction.uuid)) {
              uniqueEmployers.set(transaction.uuid, transaction.name);
            }
          }
        });
        
        employerOptions = Array.from(uniqueEmployers.entries()).map(([uuid, name]) => ({
          uuid,
          name
        }));
      }
    } catch (err) {
      console.error('Error loading employer options:', err);
    }
  }

  function handleApplyFilter() {
    currentPage = 1;
    loadTransactions();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    loadTransactions();
  }

  function renderStatus(status: string): string {
    if (!status) return 'N/A';
    
    // Map string status to PayoutStatusEnum values
    let payoutStatus;
    switch (status.toLowerCase()) {
      case 'completed':
      case 'processed':
        payoutStatus = 'COMPLETED';
        break;
      case 'failed':
      case 'cancelled':
        payoutStatus = 'FAILED';
        break;
      case 'pending':
      case 'processing':
      default:
        payoutStatus = 'PENDING';
        break;
    }
    
    return `<div class="status-badge-wrapper" data-status="${payoutStatus}"></div>`;
  }

  function renderActions(value: any, row: any): string {
    return `<button class="view-btn" onclick="handleViewClick('${row.uuid}')">View Details</button>`;
  }

  function handleViewClick(uuid: string) {
    console.log('View transaction with UUID:', uuid);
    // Add navigation logic here when needed
    // For example: push(`#/transaction-history/${uuid}`);
  }

  // Make the function globally available for onclick handlers
  if (typeof window !== 'undefined') {
    (window as any).handleViewClick = handleViewClick;
  }
</script>

  <div class="payroll-transaction-history-page">
    <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-row">
      <div class="filter-item">
        <Dropdown
          options={employerOptions}
          selectedValue={selectedEmployer}
          placeholder="All Employers"
          onSelectionChange={(value) => selectedEmployer = value}
        />
      </div>
      
      <div class="filter-item date-range">
        <DateRange bind:startDate bind:endDate />
      </div>
      
      <div class="filter-item">
        <Button variant="primary" on:click={handleApplyFilter}>
          Apply Filter
        </Button>
      </div>
    </div>
  </div>

  <!-- Transactions List Section -->
  <div class="transactions-section">
    {#if error}
      <div class="error-message">
        <p>{error}</p>
        <Button variant="secondary" on:click={loadTransactions}>Try Again</Button>
      </div>
    {:else if isLoading}
      <div class="loading-message">Loading transactions...</div>
    {:else if !transactions || transactions.length === 0}
      <div class="empty-message">No transactions found</div>
    {:else}
      <Table
        columns={tableColumns}
        data={transactions}
      />
    {/if}

    {#if totalPages > 1}
      <div class="pagination-wrapper">
        <Pagination
          {currentPage}
          {totalPages}
          on:pageChange={handlePageChange}
        />
      </div>
    {/if}
  </div>
</div>

<style>
  .payroll-transaction-history-page {
    padding: 0;
    max-width: 100%;
  }



  /* Filters Section */
  .filters-section {
    padding: 1.5rem 2rem;
  }

  .filters-row {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: flex-start;
  }

  .filter-item {
    flex: 0 0 auto;
  }

  .filter-item:nth-child(1) {
    min-width: 200px;
  }

  .filter-item.date-range {
    min-width: 250px;
    flex: 0 0 250px;
  }

  .filter-item:last-child {
    margin-left: auto;
    min-width: 120px;
  }

  /* Transactions Section */
  .transactions-section {
    border-radius: 0 0 12px 12px;
    padding: 1.5rem 2rem;
  }

  .error-message {
    text-align: center;
    padding: 3rem;
    color: #ef4444;
  }

  .error-message p {
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  .loading-message,
  .empty-message {
    text-align: center;
    padding: 3rem;
    color: #6b7280;
    font-size: 1rem;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Status badges - More specific targeting */
  :global(.status-badge) {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0.375rem 0.75rem !important;
    border-radius: 9999px !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1 !important;
    white-space: nowrap !important;
  }

  :global(.status-badge svg) {
    width: 16px !important;
    height: 16px !important;
    margin-right: 0.25rem !important;
    flex-shrink: 0 !important;
    display: block !important;
  }

  :global(.status-badge span) {
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  :global(.status-badge.pending) {
    background-color: #FFF8E1 !important;
    color: #F57C00 !important;
  }

  :global(.status-badge.completed) {
    background-color: #E6F4EA !important;
    color: #34A853 !important;
  }

  :global(.status-badge.failed) {
    background-color: #FDEDEB !important;
    color: #EA4335 !important;
  }

  :global(.view-btn) {
    background: transparent !important;
    color: #6b7280 !important;
    border: none !important;
    border-radius: 4px !important;
    padding: 0.5rem 0.75rem !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: color 0.2s !important;
  }

  :global(.view-btn:hover) {
    color: #374151 !important;
  }

  /* Responsive design */
  @media (max-width: 1024px) {
    /* Header is hidden, no responsive adjustments needed */
  }

  @media (max-width: 768px) {
    .payroll-transaction-history-page {
      padding: 1rem;
    }

    .filters-section {
      padding: 1rem;
    }

    .transactions-section {
      padding: 1rem;
    }

    .filters-row {
      flex-direction: column;
      align-items: stretch;
    }

    .filter-item {
      min-width: unset;
    }

    .filter-item.date-range {
      min-width: unset;
    }

    .filter-item:last-child {
      margin-left: unset;
    }
  }
</style> 